import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../store/AuthContext';
import { AuthService } from '../../services/api';
// Import the onboarding style wrapper
import OnboardingStyleWrapper from '../../components/ui/OnboardingStyleWrapper';

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [role, setRole] = useState<'HOMEOWNER' | 'TRADESPERSON'>('HOMEOWNER');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [loading, setLoading] = useState(false);

  // Effect to redirect user after auth state updates
  React.useEffect(() => {
    if (user) {
      if (!user.isOnboarded) {
        navigate('/onboarding');
      } else {
        navigate('/dashboard');
      }
    }
  }, [user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      alert('Passwords do not match');
      return;
    }
    if (!agreeTerms) {
      alert('You must agree to the terms');
      return;
    }

    try {
      setLoading(true);
      console.log('Attempting to register with data:', {
        email,
        password,
        firstName,
        lastName,
        role,
      });
      
      // Register the user
      const registerResponse = await AuthService.register({
        email,
        password,
        firstName,
        lastName,
        role,
      });
      
      console.log('Registration response:', registerResponse);
      
      // After successful registration, automatically log in the user
      try {
        await AuthService.login(email, password);
        console.log('Auto-login successful');
        // Navigation will be handled by the useEffect above
      } catch (loginError) {
        console.error('Auto-login error:', loginError);
        // If auto-login fails, redirect to login page
        navigate('/login');
      }
    } catch (err: any) {
      console.error('Registration error:', err);
      const msg = err?.response?.data?.error || err?.message || 'Registration failed';
      alert(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md mx-auto">
        <OnboardingStyleWrapper className="mb-8 text-center">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 mb-4">
            <svg className="h-8 w-8 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            </svg>
          </div>
          <h2 className="onboarding-title text-3xl">
            Create your account
          </h2>
          <p className="onboarding-subtitle mt-2 text-sm">
            Or{' '}
            <a href="/login" className="font-medium text-blue-600 hover:text-blue-500">
              sign in to your existing account
            </a>
          </p>
        </OnboardingStyleWrapper>

        <OnboardingStyleWrapper>
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                I am a:
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="relative cursor-pointer">
                  <input
                    type="radio"
                    name="role"
                    value="HOMEOWNER"
                    checked={role === 'HOMEOWNER'}
                    onChange={() => setRole('HOMEOWNER')}
                    className="sr-only"
                  />
                  <div className={`border-2 rounded-lg p-4 text-center transition-colors ${
                    role === 'HOMEOWNER' 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-300 hover:border-blue-500'
                  }`}>
                    <div className="text-2xl mb-2">🏠</div>
                    <div className="font-medium">Homeowner</div>
                    <div className="text-xs text-gray-500">Looking for tradespeople</div>
                  </div>
                </label>
                <label className="relative cursor-pointer">
                  <input
                    type="radio"
                    name="role"
                    value="TRADESPERSON"
                    checked={role === 'TRADESPERSON'}
                    onChange={() => setRole('TRADESPERSON')}
                    className="sr-only"
                  />
                  <div className={`border-2 rounded-lg p-4 text-center transition-colors ${
                    role === 'TRADESPERSON' 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-300 hover:border-blue-500'
                  }`}>
                    <div className="text-2xl mb-2">🔨</div>
                    <div className="font-medium">Tradesperson</div>
                    <div className="text-xs text-gray-500">Offering services</div>
                  </div>
                </label>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                  First name
                </label>
                <div className="mt-1">
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    required
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="onboarding-input"
                    placeholder="John"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                  Last name
                </label>
                <div className="mt-1">
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    required
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="onboarding-input"
                    placeholder="Smith"
                  />
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <div className="mt-1">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="onboarding-input"
                    placeholder="john@example.com"
                  />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="mt-1">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="onboarding-input"
                    placeholder="Create a secure password"
                  />
              </div>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Confirm password
              </label>
              <div className="mt-1">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="onboarding-input"
                    placeholder="Confirm your password"
                  />
              </div>
            </div>

            <div className="flex items-center">
              <input
                id="agree-terms"
                name="agree-terms"
                type="checkbox"
                required
                checked={agreeTerms}
                onChange={(e) => setAgreeTerms(e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="agree-terms" className="ml-2 block text-sm text-gray-900">
                I agree to the{' '}
                <a href="#" className="text-blue-600 hover:text-blue-500">
                  Terms of Service
                </a>{' '}
                and{' '}
                <a href="#" className="text-blue-600 hover:text-blue-500">
                  Privacy Policy
                </a>
              </label>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="onboarding-button-primary w-full"
              >
                {loading ? 'Creating account...' : 'Create account'}
              </button>
            </div>
          </form>
        </OnboardingStyleWrapper>
      </div>
    </div>
  );
};

export default RegisterPage;